import { z } from "zod";

import { McpToolAnnotationsSchema } from "./McpToolAnnotations.schema";
import { McpToolInputSchemaSchema } from "./McpToolInputSchema.schema";
import { McpToolOutputSchemaSchema } from "./McpToolOutputSchema.schema";

/**
 * `McpServerTool.discoveredContract` and `McpServerTool.approvedContract` — one
 * shape, two columns.
 *
 * It is the MCP `Tool` object exactly as the server advertised it, minus `_meta`
 * and `icons` (see `toMcpToolContract`). Every string in it is SERVER-AUTHORED and
 * untrusted: `description` and `title` are what a tool-poisoning attack edits, and
 * that is why they are inside the hashed contract rather than beside it.
 *
 * Loose on purpose. A field the protocol adds later is kept rather than stripped,
 * so it is part of the hash and a server that starts sending it reads as CHANGED
 * rather than silently becoming a different tool.
 *
 * No `_v`: the object is the protocol's, not ours, and its stored bytes are pinned
 * by `McpServerTool.discoveredSchemaHash` — a version key added here would change
 * every hash. The same exception `ProviderHandshake/payload/__json.schema.ts`
 * documents for its own externally shaped payloads.
 *
 * No `.default()` and no transform anywhere in this tree, and that is load-bearing:
 * parsing must return the input unchanged, or the stored contract would hash
 * differently from the one the server sent.
 */
export const McpToolContractSchema = z.looseObject({
  name: z.string().min(1),
  title: z.string().optional(),
  description: z.string().optional(),
  inputSchema: McpToolInputSchemaSchema,
  outputSchema: McpToolOutputSchemaSchema.optional(),
  annotations: McpToolAnnotationsSchema.optional()
});

export type McpToolContract = z.infer<typeof McpToolContractSchema>;
